import React from "react";
import { CircularProgress } from "@mui/material";
import { FaChevronDown, FaChevronUp, FaNoteSticky } from "react-icons/fa6";
import { getClinicalRecords } from "requests/ClinicalRecord";
import { IPreviousObservation } from "shared/types/PreviousObservations";
import { useAuth } from "shared/providers/auth";
import * as Styled from "./styles";

interface IPreviousObservationsCardProps {
  pacienteId: number;
  maxObservations?: number;
}

const PreviousObservationsCard: React.FC<IPreviousObservationsCardProps> = ({
  pacienteId,
  maxObservations = 3,
}) => {
  const { user } = useAuth();
  const [observations, setObservations] = React.useState<IPreviousObservation[]>([]);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);

  const fetchObservations = React.useCallback(async () => {
    if (!pacienteId || pacienteId <= 0) {
      setLoading(false);
      return;
    }

    try {
      const { status, data } = await getClinicalRecords({
        userId: user.data.id,
        idPac: pacienteId,
      });

      if ([200, 201].includes(status) && data.success && data.data.length > 0) {
        const extractedObservations: IPreviousObservation[] = [];

        for (const record of data.data) {
          let observacaoText = "";

          if (record.doc && record.doc.procedures && Array.isArray(record.doc.procedures)) {
            const obsItem = record.doc.procedures.find(
              (item: any) => item.question === "Observações" && item.description
            );

            if (obsItem && obsItem.description) {
              observacaoText = obsItem.description;
            }
          }

          if (record.nextAppointment && Array.isArray(record.nextAppointment)) {
            const planningObs = record.nextAppointment.find(
              (item: any) => item.id === "PPQ014" && item.description
            );

            if (planningObs && planningObs.description) {
              if (observacaoText) {
                observacaoText += "\n\n📋 Planejamento: " + planningObs.description;
              } else {
                observacaoText = planningObs.description;
              }
            }
          }

          if (observacaoText) {
            extractedObservations.push({
              id: record.id,
              observacao: observacaoText,
              data: record.appointment,
            });
          }

          if (extractedObservations.length >= maxObservations) {
            break;
          }
        }

        setObservations(extractedObservations);
        if (extractedObservations.length > 0) {
          setIsExpanded(true);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar observações anteriores:", error);
    } finally {
      setLoading(false);
    }
  }, [pacienteId, user.data.id, maxObservations]);

  React.useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  // Não renderizar se não houver observações e não estiver carregando
  if (!loading && observations.length === 0) {
    return null;
  }

  return (
    <Styled.Container>
      <Styled.Card>
        <Styled.Header onClick={() => setIsExpanded(!isExpanded)}>
          <Styled.Title>
            <FaNoteSticky />
            Observações da(s) Consulta(s) Anterior(es)
            {observations.length > 0 && (
              <Styled.Badge>{observations.length}</Styled.Badge>
            )}
          </Styled.Title>
          <Styled.ToggleButton>
            {isExpanded ? (
              <>
                Ocultar <FaChevronUp />
              </>
            ) : (
              <>
                Expandir <FaChevronDown />
              </>
            )}
          </Styled.ToggleButton>
        </Styled.Header>

        <Styled.Content $isExpanded={isExpanded}>
          {loading ? (
            <Styled.LoadingContainer>
              <CircularProgress size={24} />
            </Styled.LoadingContainer>
          ) : observations.length > 0 ? (
            observations.map((obs) => (
              <Styled.ObservationItem key={obs.id}>
                <Styled.ObservationDate>
                  📅 {obs.data}
                </Styled.ObservationDate>
                <Styled.ObservationText>
                  {obs.observacao}
                </Styled.ObservationText>
              </Styled.ObservationItem>
            ))
          ) : (
            <Styled.NoObservations>
              Nenhuma observação anterior encontrada.
            </Styled.NoObservations>
          )}
        </Styled.Content>
      </Styled.Card>
    </Styled.Container>
  );
};

export { PreviousObservationsCard };
